/**
 * Comprehensive Doctor Management API Service
 * Handles all doctor management features including profile, availability, 
 * patients, consultations, prescriptions, billing, and analytics
 */
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// Use proxy for development, fallback to config
const BASE_URL = process.env.NODE_ENV === 'development' ? '' : API_BASE_URL;
const DOCTOR_API = `${BASE_URL}/api/v1/doctors/management`;

// Doctor Profile API
export const doctorProfileApi = {
  // Get doctor profile
  getProfile: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${DOCTOR_API}/profile/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching doctor profile:', error);
      throw error;
    }
  },

  // Update doctor profile
  updateProfile: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`${DOCTOR_API}/update_profile/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating doctor profile:', error);
      throw error;
    }
  }
};

// Doctor Availability API
export const doctorAvailabilityApi = {
  // Get availability schedule
  getAvailability: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${DOCTOR_API}/availability/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching availability:', error);
      throw error;
    }
  },

  // Set availability schedule
  setAvailability: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${DOCTOR_API}/set_availability/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error setting availability:', error);
      throw error;
    }
  }
};

// Doctor Patient Management API
export const doctorPatientApi = {
  // Get patient list
  getPatients: async (filters = {}) => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${DOCTOR_API}/patients/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  },

  // Get patient details
  getPatientDetails: async (patientId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${DOCTOR_API}/patient_details/${patientId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching patient details:', error);
      throw error;
    }
  }
};

// Doctor Consultation API
export const doctorConsultationApi = {
  // Get consultations
  getConsultations: async (filters = {}) => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${DOCTOR_API}/consultations/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consultations:', error);
      throw error;
    }
  },

  // Add consultation note
  addConsultationNote: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${DOCTOR_API}/add_consultation_note/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error adding consultation note:', error);
      throw error;
    }
  }
};

// Doctor Prescription API
export const doctorPrescriptionApi = {
  // Get prescriptions
  getPrescriptions: async (filters = {}) => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${DOCTOR_API}/prescriptions/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
      throw error;
    }
  },

  // Create prescription
  createPrescription: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${DOCTOR_API}/create_prescription/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating prescription:', error);
      throw error;
    }
  }
};

// Doctor Billing API
export const doctorBillingApi = {
  // Get billing information
  getBilling: async (filters = {}) => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${DOCTOR_API}/billing/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching billing:', error);
      throw error;
    }
  }
};

// Doctor Analytics API
export const doctorAnalyticsApi = {
  // Get analytics data
  getAnalytics: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${DOCTOR_API}/analytics/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }
};

// Doctor Verification API
export const doctorVerificationApi = {
  // Get verification status
  getVerificationStatus: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${DOCTOR_API}/verification_status/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching verification status:', error);
      throw error;
    }
  },

  // Submit verification documents
  submitVerification: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${DOCTOR_API}/submit_verification/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting verification:', error);
      throw error;
    }
  }
};

export default {
  doctorProfileApi,
  doctorAvailabilityApi,
  doctorPatientApi,
  doctorConsultationApi,
  doctorPrescriptionApi,
  doctorBillingApi,
  doctorAnalyticsApi,
  doctorVerificationApi
};


















